/**
 * SectorCombobox — V23.12
 *
 * Combobox accessible pour saisir un secteur d'activité.
 * - ~95 secteurs catégorisés (Finance, Immobilier, Santé, Tech, etc.)
 * - Recherche live (case + accent insensible)
 * - Saisie libre acceptée → ping POST /api/sectors/suggest pour apprentissage
 * - Catégorisation visible dans la dropdown
 * - Affiche "Autre / saisie libre" en bas de liste si la valeur tapée ne correspond pas
 *
 * Drop-in remplacement pour les <select> "Secteur" existants.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { Search, X, Tag, ChevronDown } from "lucide-react";
import { SECTOR_CATALOG, searchSectors, isCatalogValue } from "@/data/sectorCatalog";
import { api } from "@/lib/api";

export default function SectorCombobox({
  value = "",
  onChange,
  placeholder = "Sélectionner un secteur…",
  testId = "sector-combobox",
  disabled = false,
  className = "",
}) {
  const [query, setQuery] = useState(value || "");
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(0);
  const inputRef = useRef(null);
  const containerRef = useRef(null);

  useEffect(() => { setQuery(value || ""); }, [value]);

  // Close on outside click
  useEffect(() => {
    const onClick = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const suggestions = useMemo(() => searchSectors(query, 12), [query]);
  const isCustom = query.trim().length >= 2 && !isCatalogValue(query);

  const commit = (val) => {
    const v = (val || "").trim();
    setQuery(v);
    setOpen(false);
    onChange?.(v);
    // Apprentissage : si valeur libre non présente au catalogue → log côté backend
    if (v && !isCatalogValue(v)) {
      api.post("/sectors/suggest", { value: v }).catch(() => {});
    }
  };

  const onKey = (e) => {
    if (!open) {
      if (e.key === "ArrowDown" || e.key === "Enter") { setOpen(true); return; }
      return;
    }
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlight((h) => Math.min(suggestions.length, h + 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlight((h) => Math.max(0, h - 1));
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (highlight < suggestions.length) {
        commit(suggestions[highlight].label);
      } else if (isCustom) {
        commit(query);
      }
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  };

  return (
    <div ref={containerRef} className={`relative ${className}`} data-testid={testId}>
      <div className="relative">
        <Search className="w-3.5 h-3.5 text-zinc-500 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={(e) => { setQuery(e.target.value); setOpen(true); setHighlight(0); }}
          onFocus={() => setOpen(true)}
          onKeyDown={onKey}
          placeholder={placeholder}
          disabled={disabled}
          className="w-full bg-black/30 border border-white/10 rounded-md pl-9 pr-9 py-2 text-sm text-white placeholder:text-zinc-600 focus:border-[#378ADD] focus:outline-none disabled:opacity-50"
          data-testid={`${testId}-input`}
        />
        {query ? (
          <button
            type="button"
            onClick={() => { setQuery(""); onChange?.(""); setOpen(true); inputRef.current?.focus(); }}
            className="absolute right-2 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-white"
            tabIndex={-1}
            data-testid={`${testId}-clear`}
          >
            <X className="w-3.5 h-3.5" />
          </button>
        ) : (
          <ChevronDown className="w-3.5 h-3.5 text-zinc-500 absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none" />
        )}
      </div>

      {open && (
        <div className="absolute z-50 mt-1 w-full max-h-72 overflow-y-auto bg-[#0d1626] border border-white/10 rounded-md shadow-xl" data-testid={`${testId}-dropdown`}>
          {suggestions.length === 0 && !isCustom && (
            <div className="px-3 py-2 text-[11px] text-zinc-500">
              Tapez au moins 2 caractères ou parcourez les catégories ci-dessous.
            </div>
          )}

          {suggestions.length > 0 && (() => {
            const blocks = [];
            let lastCat = null;
            suggestions.forEach((s, i) => {
              if (s.category !== lastCat) {
                blocks.push(
                  <div key={`cat-${s.category}`} className="px-3 py-1 text-[9px] uppercase tracking-wider text-zinc-500 bg-white/[0.02] border-b border-white/5">
                    {s.category}
                  </div>
                );
                lastCat = s.category;
              }
              blocks.push(
                <button
                  type="button"
                  key={`opt-${s.label}-${i}`}
                  onClick={() => commit(s.label)}
                  onMouseEnter={() => setHighlight(i)}
                  className={`w-full text-left px-3 py-1.5 text-[12px] flex items-center gap-2 ${
                    i === highlight ? "bg-[#378ADD]/15 text-white" : "text-zinc-200 hover:bg-white/5"
                  }`}
                  data-testid={`${testId}-option-${i}`}
                >
                  <Tag className="w-2.5 h-2.5 text-zinc-500" />
                  {s.label}
                </button>
              );
            });
            return blocks;
          })()}

          {isCustom && (
            <button
              type="button"
              onClick={() => commit(query)}
              className={`w-full text-left px-3 py-2 text-[12px] border-t border-white/5 ${
                highlight === suggestions.length
                  ? "bg-[#EF9F27]/15 text-[#EF9F27]"
                  : "text-[#EF9F27] hover:bg-[#EF9F27]/10"
              }`}
              data-testid={`${testId}-custom-commit`}
            >
              <span className="text-[9px] uppercase tracking-wider mr-2 opacity-70">Autre / saisie libre</span>
              <strong>&laquo;{query}&raquo;</strong>
            </button>
          )}
        </div>
      )}
    </div>
  );
}

// Helper qui expose le catalogue pour les autres composants (par ex. Admin).
export { SECTOR_CATALOG };
